import {
  inlineMdComponents,
  mermaidConfig,
  normalizeHeadingDepthWithMap,
  shikiTheme,
  streamdownPlugins,
  streamdownRehypePlugins,
  type MarkdownComponents,
} from '@heidi/answer-kit';
import { useMemo, type JSX } from 'react';
import { Streamdown } from 'streamdown';
import type { Annotation } from '../../contracts';
import { CitationBadge, type CitationSource } from './CitationBadge';

interface AnnotatedTextProps {
  text: string;
  annotations: Annotation[];
  className?: string;
  isAnimating?: boolean;
  /**
   * EVI-1486: analytics IDs threaded through to the rendered
   * `CitationBadge`s. Only used by the legacy (non-Evidence) prose path.
   */
  chatId?: string;
  messageId?: string;
  sessionId?: string;
}

interface CitationGroup {
  endIndex: number;
  sources: CitationSource[];
}

/**
 * Groups annotations by their end_index position and converts to CitationSource format.
 * Multiple citations can share the same end position.
 */
function groupAnnotationsByPosition(annotations: Annotation[]): CitationGroup[] {
  if (!annotations.length) return [];

  const groups = new Map<number, CitationSource[]>();

  for (const annotation of annotations) {
    const endIndex = annotation.end_index;
    const source: CitationSource = {
      url: annotation.url,
      title: annotation.title,
    };

    if (groups.has(endIndex)) {
      groups.get(endIndex)!.push(source);
    } else {
      groups.set(endIndex, [source]);
    }
  }

  // Sort by position ascending
  return Array.from(groups.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([endIndex, sources]) => ({ endIndex, sources }));
}

/**
 * AnnotatedText - Renders text with inline citation badges at specified positions.
 *
 * Takes text and annotations from the backend, groups citations by their end_index,
 * and renders the text with CitationBadge components inserted at citation positions.
 *
 * Example:
 * - Text: "Studies show improved outcomes."
 * - Annotation: { end_index: 30, url: "...", title: "..." }
 * - Renders: "Studies show improved outcomes.[Badge]"
 */
export const AnnotatedText = ({
  text,
  annotations,
  className,
  isAnimating,
  chatId,
  messageId,
  sessionId,
}: AnnotatedTextProps): JSX.Element => {
  // Heading levels are rewritten to outline depth before anything is sliced.
  // That changes the text length, and `end_index` is a backend offset into the
  // original, so every citation position is carried through the same map.
  const { text: prose, mapIndex } = useMemo(
    () => normalizeHeadingDepthWithMap(text),
    [text]
  );

  const citationGroups = useMemo(
    () =>
      groupAnnotationsByPosition(annotations).map((group) => ({
        ...group,
        endIndex: mapIndex(group.endIndex),
      })),
    [annotations, mapIndex]
  );

  // If no annotations, render text directly with markdown
  if (!citationGroups.length) {
    return (
      <div className={className}>
        <Streamdown
          components={inlineMdComponents as MarkdownComponents}
          isAnimating={isAnimating}
          mode={isAnimating ? undefined : 'static'}
          controls={{ table: true, code: true }}
          tableMaxHeight={0}
          plugins={streamdownPlugins}
          mermaid={mermaidConfig}
          shikiTheme={shikiTheme}
          rehypePlugins={streamdownRehypePlugins}
        >
          {prose}
        </Streamdown>
      </div>
    );
  }

  // Build segments: text chunks alternating with citation badges
  const segments: JSX.Element[] = [];
  let lastIndex = 0;

  citationGroups.forEach((group, groupIndex) => {
    // Clamp endIndex to valid range
    const endIndex = Math.min(group.endIndex, prose.length);

    // Extract text segment from lastIndex to endIndex
    if (endIndex > lastIndex) {
      const textSegment = prose.slice(lastIndex, endIndex);
      // Use position-based key (lastIndex-endIndex) for stable identification
      segments.push(
        <Streamdown
          key={`text-${lastIndex}-${endIndex}`}
          components={inlineMdComponents as MarkdownComponents}
          isAnimating={isAnimating}
          mode={isAnimating ? undefined : 'static'}
          controls={{ table: true, code: true }}
          tableMaxHeight={0}
          plugins={streamdownPlugins}
          mermaid={mermaidConfig}
          shikiTheme={shikiTheme}
          rehypePlugins={streamdownRehypePlugins}
        >
          {textSegment}
        </Streamdown>
      );
    }

    // Insert citation badge at this position
    // Use endIndex + first source URL for unique key
    const citationKey = `citation-${endIndex}-${group.sources[0]?.url ?? 'unknown'}`;
    segments.push(
      <CitationBadge
        key={citationKey}
        sources={group.sources}
        chatId={chatId}
        messageId={messageId}
        sessionId={sessionId}
        // EVI-1486 round 4: legacy-mode prose. citationNumber is the 1-based
        // ordinal of this citation group within the text chunk.
        surfaceBlock="prose"
        citationNumber={groupIndex + 1}
        className="mx-0.5 align-middle"
      />
    );

    lastIndex = endIndex;
  });

  // Render remaining text after last citation
  if (lastIndex < prose.length) {
    const remainingText = prose.slice(lastIndex);
    segments.push(
      <Streamdown
        key="text-final"
        components={inlineMdComponents as MarkdownComponents}
        isAnimating={isAnimating}
        mode={isAnimating ? undefined : 'static'}
        controls={{ table: true, code: true }}
        tableMaxHeight={0}
        plugins={streamdownPlugins}
        mermaid={mermaidConfig}
        shikiTheme={shikiTheme}
        rehypePlugins={streamdownRehypePlugins}
      >
        {remainingText}
      </Streamdown>
    );
  }

  return <div className={className}>{segments}</div>;
};
